import { Box, Button, Flex, Heading, Skeleton, Text } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Image, LabelText } from "../../components";

import { FaPlay } from "react-icons/fa";
import { MovieDetailCardProps } from "../../@types/MovieDetailCard";
import ShowCaseTrailerModal from "../ShowCaseTrailerModal";
import MovieCastCardGrouped from "./MovieCastCard/MovieCastCardGrouped";

const ReactPlayer = lazy(() => import("react-player"));

function MovieDetailCardWithVideo({
  movieTitle,
  movieDescription,
  genre,
  duration,
  director,
  cast,
  release_date,
  videoLink,
  poster,
  banner,
  displayImageBackground = false,
  mobileLayout = "stacked",
  showVideo = false,
  show_cast_card = false,
  show_director_card = false,
  detailContainerBoxProp,
  labelTextProps,
  movieTitleProps,
  movieGenreProps,
  movieDescriptionProps,
  movieCardBoxProps,
  movieImageProps,
  showTrailerButtonProps,
  heroBackdropProps,
  heroContentProps,
  posterThumbProps,
  chipProps,
  showBannerPlayIcon = false,
  bannerPlayIconProps,
  displayBackgroundImageProps,
  displayBackgroundBoxProps,
  bannerGradientColor = "gray.900",
  showFirstGradientBox = true,
  showSecondGradientBox = true,
  showThirdGradientBox = true,
  firstGradientBoxProps,
  secondGradientBoxProps,
  thirdGradientBoxProps,
  movieImageWrapperProps,
}: MovieDetailCardProps) {
  const defaultLabelTextProps = {
    // A 10% label column is unreadably narrow on mobile (label wraps char by
    // char); give it a sensible mobile basis and tighten on larger screens.
    flexBasis: showVideo ? "none" : { base: "30%", sm: "20%", lg: "10%" },
  };
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  // Collapsed description in the hero mobile layout (keeps the header short).
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (showVideo) {
        if (!videoLink) {
          throw new Error("Video link is required");
        }
        setVideoUrl(videoLink);
      }
    }
  }, [videoLink, showVideo]);

  const displayBackground = displayImageBackground && banner;
  // "split" keeps poster + info side-by-side (left-aligned) on mobile instead
  // of stacking them centered — reads more like a native app detail screen.
  const isSplit = mobileLayout === "split" && !showVideo;
  // "hero" renders a dedicated native-app detail screen on mobile only.
  const isHero = mobileLayout === "hero" && !showVideo;
  const backdropSrc = banner || poster;

  // Genre / duration / release condensed into chips so they live in the header
  // instead of a tall meta block below (keeps shows above the fold).
  const metaChips = [genre, duration, release_date].filter(Boolean) as string[];

  // Native-app style mobile detail screen: a compact backdrop with the poster +
  // title + meta chips + a short (expandable) description overlaid, so the show
  // list sits close to the top. Only Director/Cast remain below.
  const heroMobile = isHero ? (
    <Box display={{ base: "block", md: "none" }} {...detailContainerBoxProp}>
      <Box position="relative" overflow="hidden" {...heroBackdropProps}>
        {backdropSrc && (
          <Image
            src={backdropSrc}
            alt={movieTitle}
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit="cover"
            objectPosition="center top"
          />
        )}
        {/* Scrim so the overlaid content is always legible */}
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-t, var(--hero-scrim, rgba(10,12,16,0.97)) 18%, rgba(10,12,16,0.6) 55%, rgba(10,12,16,0.35) 100%)"
        />
        <Flex
          position="relative"
          pt="3.5rem"
          p="1rem"
          gap="0.875rem"
          align="flex-end"
          color="white"
          {...heroContentProps}
        >
          <Image
            src={poster}
            alt={movieTitle}
            w="5.5rem"
            flexShrink={0}
            borderRadius="0.75rem"
            boxShadow="0 12px 30px -8px rgba(0,0,0,0.8)"
            objectFit="cover"
            {...posterThumbProps}
          />
          <Box minW={0} pb="0.25rem">
            <Heading
              as="h1"
              fontSize="1.35rem"
              lineHeight="1.2"
              textTransform="capitalize"
              noOfLines={2}
              textShadow="0 2px 10px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.8)"
              {...movieTitleProps}
            >
              {movieTitle}
            </Heading>
            <Flex mt="0.5rem" gap="0.4rem" flexWrap="wrap">
              {metaChips.map((chip, i) => (
                <Box
                  key={`chip_${i}`}
                  as="span"
                  fontSize="0.7rem"
                  fontWeight={600}
                  px="0.6rem"
                  py="0.2rem"
                  borderRadius="full"
                  bg="whiteAlpha.300"
                  backdropFilter="blur(8px)"
                  textTransform="capitalize"
                  {...chipProps}
                >
                  {chip}
                </Box>
              ))}
            </Flex>
          </Box>
        </Flex>

        {(movieDescription || director || cast) && (
          <Box position="relative" px="1rem" pb="1rem" color="whiteAlpha.900">
            {movieDescription && (
              <Box mb="0.5rem">
                <Text
                  fontWeight="normal"
                  fontSize="0.85rem"
                  noOfLines={descExpanded ? undefined : 2}
                  {...labelTextProps}
                  {...movieDescriptionProps}
                >
                  {movieDescription}
                </Text>
                {movieDescription.length > 90 && (
                  <Text
                    as="button"
                    type="button"
                    onClick={() => setDescExpanded((v) => !v)}
                    mt="0.15rem"
                    fontSize="0.8rem"
                    fontWeight={700}
                    color="primary.400"
                  >
                    {descExpanded ? "View less" : "View more"}
                  </Text>
                )}
              </Box>
            )}
            {director && (
              <LabelText
                label="director"
                text={director}
                fontSize="0.85rem"
                {...labelTextProps}
              />
            )}
            {cast && (
              <LabelText
                label="cast"
                text={cast}
                fontSize="0.85rem"
                {...labelTextProps}
              />
            )}
          </Box>
        )}
      </Box>

      {videoLink && (
        <Box px="1rem" pt="0.75rem">
          <ShowCaseTrailerModal
            url={videoLink}
            actionEl={
              <Button
                w="full"
                rounded="full"
                leftIcon={<FaPlay size="11px" />}
                variant="solid"
                {...showTrailerButtonProps}
              >
                Watch Trailer
              </Button>
            }
          />
        </Box>
      )}
    </Box>
  ) : null;

  return (
    <>
      {heroMobile}
      <Box
        // When the hero mobile layout is on, the standard card only shows ≥ md.
        display={isHero ? { base: "none", md: "block" } : undefined}
        position="relative"
        p={displayBackground ? { base: "2rem", md: "4rem" } : "1rem"}
        px={displayBackground ? { base: "2rem", md: "4rem" } : "1.25rem"}
        minH={displayBackground ? { base: "auto", lg: "25rem" } : "auto"}
        background={displayBackground ? bannerGradientColor : "primary.200"}
        borderRadius={displayBackground ? "24px" : "none"}
        overflow="hidden"
        boxShadow={displayBackground ? "dark-lg" : "none"}
        itemScope
        itemProp=""
        color={displayBackground ? "white" : "inherit"}
        textAlign={{
          base: isSplit ? "left" : "center",
          xl: "left",
        }}
        {...detailContainerBoxProp}
      >
        {displayBackground && (
          <Box
            position="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            zIndex={0}
            {...displayBackgroundBoxProps}
          >
            <Image
              src={banner}
              alt="Movie Billboard"
              w="100%"
              h="100%"
              objectFit="cover"
              objectPosition="center top"
              {...displayBackgroundImageProps}
            />
            {showFirstGradientBox && (
              <Box
                position="absolute"
                inset={0}
                bgGradient={{
                  base: `linear(to-t, ${bannerGradientColor} 0%, ${bannerGradientColor} 20%, transparent 80%)`,
                  lg: `linear(to-r, ${bannerGradientColor} 0%, ${bannerGradientColor} 25%, ${bannerGradientColor} 40%, transparent 100%)`,
                }}
                {...firstGradientBoxProps}
              />
            )}
            {showSecondGradientBox && (
              <Box
                position="absolute"
                inset={0}
                bgGradient={`linear(to-b, transparent 30%, ${bannerGradientColor} 95%)`}
                {...secondGradientBoxProps}
              />
            )}
            {showThirdGradientBox && (
              <Box
                position="absolute"
                inset={0}
                bg={"blackAlpha.200"}
                {...thirdGradientBoxProps}
              />
            )}
          </Box>
        )}

        <Flex
          position="relative"
          zIndex={1}
          gap={{ base: isSplit ? "1rem" : "1.5rem", lg: "2.5rem" }}
          direction={{
            base: isSplit ? "row" : "column",
            lg: "row",
          }}
          alignItems={{
            base: isSplit ? "flex-start" : "center",
            lg: displayBackground ? "flex-end" : "flex-start",
          }}
          {...movieCardBoxProps}
        >
          {showVideo ? (
            <Box
              flexBasis={{
                base: "none",
                lg: "40%",
              }}
              height={{
                base: "25rem",
                lg: "22rem",
              }}
              width="100%"
            >
              {videoUrl && (
                <Suspense
                  fallback={
                    <Skeleton width="100%" height="100%" borderRadius="md" />
                  }
                >
                  <ReactPlayer url={videoUrl} width="100%" height="100%" />
                </Suspense>
              )}
            </Box>
          ) : (
            <Box
              position="relative"
              role="group"
              cursor="pointer"
              transition="all 0.3s ease-in-out"
              // _hover={{ transform: "scale(1.02)" }}
              flexBasis={{ base: isSplit ? "38%" : "25%", lg: "25%" }}
              flexShrink={0}
              w="full"
              maxW={{
                base: isSplit ? "11rem" : "15rem",
              }}
              maxH={{
                base: "30rem",
                lg: "30rem",
              }}
              h="full"
              {...movieImageWrapperProps}
            >
              <Image
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                  boxShadow: displayBackground
                    ? "0 25px 50px -12px rgba(0, 0, 0, 0.7)"
                    : "none",
                }}
                w="full"
                h="full"
                itemProp="image"
                itemScope
                title={movieTitle}
                itemRef="imageRef"
                objectFit="cover"
                src={poster}
                alt={movieTitle}
                {...movieImageProps}
              />
            </Box>
          )}

          <Box
            sx={{
              mt: "1rem",
            }}
            itemScope
            itemType="http://schema.org/Movie"
            w="full"
          >
            <Heading
              textTransform="capitalize"
              size={{ base: "lg", md: "xl" }}
              {...movieTitleProps}
            >
              {movieTitle}{" "}
              {videoLink && (
                <ShowCaseTrailerModal
                  url={videoLink}
                  actionEl={
                    <Button
                      size="sm"
                      rounded="full"
                      leftIcon={<FaPlay size="10px" />}
                      variant="solid"
                      bg="whiteAlpha.400"
                      color="white"
                      backdropFilter="blur(10px)"
                      border="1px solid"
                      borderColor="whiteAlpha.500"
                      _hover={{
                        transform: "scale(1.05)",
                      }}
                      transition="all 0.3s"
                      px="6"
                      mx={4}
                      {...showTrailerButtonProps}
                    >
                      Trailer
                    </Button>
                  }
                />
              )}
            </Heading>
            <Heading
              size="small"
              textTransform="uppercase"
              mt="1rem"
              itemProp="genre"
              title={genre}
              color={displayBackground ? "primary.300" : "inherit"}
              {...movieGenreProps}
            >
              {genre}
            </Heading>
            <Text
              mt="1rem"
              fontWeight="normal"
              color={displayBackground ? "whiteAlpha.800" : "surface.400"}
              itemScope
              itemProp="description"
              title={movieTitle}
              itemType="https://schema.org/Product"
              {...labelTextProps}
              {...movieDescriptionProps}
            >
              {movieDescription}
            </Text>
            <Flex
              direction="column"
              mt="1.5rem"
              gap="0.25rem"
              alignItems={{
                base: isSplit ? "flex-start" : "center",
                xl: "flex-start",
              }}
            >
              <LabelText
                itemProp="director"
                itemScope
                itemType="https://schema.org/Person"
                label="director"
                title={director}
                text={director}
                labelProps={defaultLabelTextProps}
                {...labelTextProps}
              />
              <LabelText
                itemProp="actor"
                itemScope
                itemType="https://schema.org/Person"
                title={cast}
                label="cast"
                text={cast}
                labelProps={defaultLabelTextProps}
                {...labelTextProps}
              />
              <LabelText
                itemProp="datePublished"
                itemScope
                itemType="https://schema.org/Date"
                label="release on"
                title={`${release_date}`}
                text={release_date}
                labelProps={defaultLabelTextProps}
                {...labelTextProps}
              />
              <LabelText
                itemProp="duration"
                title={duration}
                itemScope
                itemType="https://schema.org/Duration"
                label="duration"
                text={duration}
                labelProps={defaultLabelTextProps}
                {...labelTextProps}
              />
            </Flex>
          </Box>
        </Flex>

        {displayBackground && showBannerPlayIcon && videoLink && (
          <ShowCaseTrailerModal
            url={videoLink}
            actionEl={
              <Button
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                borderRadius="full"
                w="64px"
                h="64px"
                minW="unset"
                bg="whiteAlpha.200"
                backdropFilter="blur(6px)"
                border="2px solid"
                borderColor="whiteAlpha.500"
                color="white"
                _hover={{
                  bg: "whiteAlpha.400",
                  transform: "translate(-50%, -50%) scale(1.1)",
                }}
                transition="all 0.3s ease"
                zIndex={2}
                {...bannerPlayIconProps}
              >
                <FaPlay size={22} />
              </Button>
            }
          />
        )}
      </Box>

      {show_cast_card && cast && (
        <MovieCastCardGrouped
          title="Cast & Crew"
          allCastdetails={cast?.split(",") as string[]}
        />
      )}

      {show_director_card && director && (
        <MovieCastCardGrouped
          title="Director"
          allCastdetails={director?.split(",") as string[]}
        />
      )}
    </>
  );
}

export default MovieDetailCardWithVideo;
